import React from 'react';
import _ from 'lodash';
import T from 'i18n-react';
import { Grid, Paper } from '@material-ui/core';

import AddBoxIcon from '@material-ui/icons/AddBox';
import AssignmentIcon from '@material-ui/icons/Assignment';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import DescriptionIcon from '@material-ui/icons/Description';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FeedbackIcon from '@material-ui/icons/Feedback';
import FitnessCenterIcon from '@material-ui/icons/FitnessCenter';
import HealingIcon from '@material-ui/icons/Healing';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import ListAltIcon from '@material-ui/icons/ListAlt';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import LocalPharmacyIcon from '@material-ui/icons/LocalPharmacy';
import MoodIcon from '@material-ui/icons/Mood';
import NotesIcon from '@material-ui/icons/Notes';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import PersonIcon from '@material-ui/icons/Person';
import ScatterPlotIcon from '@material-ui/icons/ScatterPlot';
import SpaIcon from '@material-ui/icons/Spa';
import SubjectIcon from '@material-ui/icons/Subject';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';

import { Logger } from '@apricityhealth/web-common-lib';
import { getPatientData } from '@apricityhealth/web-common-lib/utils/Services';

import { ActionButton } from './ActionButton';

const log = new Logger();

export const ACTION_BUTTON_ICONS = {
    AddBox: <AddBoxIcon />,
    Assignment: <AssignmentIcon />,
    AssignmentTurnedIn: <AssignmentTurnedInIcon />,
    BubbleChart: <BubbleChartIcon />,
    Description: <DescriptionIcon />,
    EmojiObjects: <EmojiObjectsIcon />,
    Favorite: <FavoriteIcon />,
    Feedback: <FeedbackIcon />,
    FitnessCenter: <FitnessCenterIcon />,
    Healing: <HealingIcon />,
    HowToReg: <HowToRegIcon />,
    ListAlt: <ListAltIcon />,
    LocalHospital: <LocalHospitalIcon />,
    LocalPharmacy: <LocalPharmacyIcon />,
    Mood: <MoodIcon />,
    Notes: <NotesIcon />,
    OndemandVideo: <OndemandVideoIcon />,
    Person: <PersonIcon />,
    ScatterPlot: <ScatterPlotIcon />,
    Spa: <SpaIcon />,
    Subject: <SubjectIcon />,
    VerifiedUser: <VerifiedUserIcon />
};

export const ACTION_BUTTON_ICON_NAMES = Object.keys(ACTION_BUTTON_ICONS).sort();

function parseConfig(raw) {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
        log.error('preVisitActions parse error:', err);
        return [];
    }
}

export class PreVisitActions extends React.Component {
    constructor(props) {
        super(props);
        this.state = { visibleItems: [], loaded: false, configEmpty: true };
    }

    componentDidMount() {
        this.loadButtons();
    }

    componentDidUpdate(oldProps) {
        const oldConfig = _.get(oldProps.appContext, 'state.org.config.preVisitActions');
        const newConfig = _.get(this.props.appContext, 'state.org.config.preVisitActions');
        const oldLanguage = _.get(oldProps.appContext, 'state.language');
        const newLanguage = _.get(this.props.appContext, 'state.language');
        const oldPatientId = _.get(oldProps.appContext, 'state.patientId');
        const newPatientId = _.get(this.props.appContext, 'state.patientId');
        if (oldConfig !== newConfig || oldLanguage !== newLanguage || oldPatientId !== newPatientId) {
            this.loadButtons();
        }
    }

    async loadButtons() {
        const { appContext } = this.props;
        const { patientId, language } = appContext.state;
        const config = parseConfig(_.get(appContext, 'state.org.config.preVisitActions'));

        if (config.length === 0) {
            this.setState({ visibleItems: [], loaded: true, configEmpty: true });
            return;
        }
        if (!patientId) {
            // config has items but the patient hasn't loaded yet — wait. Keep loaded:false so
            // we don't briefly flash the "caught up" message before the gate checks can run.
            this.setState({ visibleItems: [], loaded: false, configEmpty: false });
            return;
        }

        const candidates = config.filter((item) => !item.language || item.language === language);

        const checks = await Promise.all(candidates.map(async (item) => {
            if (!item.dataId) return item;
            try {
                const result = await getPatientData(appContext, patientId, {
                    dataId: item.dataId,
                    days: item.days ?? 0,
                    historical: true
                });
                const data = (result && result.data) || [];
                return data.length === 0 ? item : null;
            } catch (err) {
                log.error('preVisitAction gate fetch failed for dataId:', item.dataId, err);
                return item;
            }
        }));

        this.setState({ visibleItems: checks.filter(Boolean), loaded: true, configEmpty: false });
    }

    onClickButton(item) {
        const { appContext } = this.props;
        appContext.setState({ models: Array.isArray(item.models) && item.models.length ? item.models : null });
    }

    render() {
        const { appContext } = this.props;
        const { visibleItems, loaded, configEmpty } = this.state;

        if (!loaded || configEmpty) return null;

        const daysUntilHomeVisit = _.get(appContext, 'state.daysUntilHomeVisit');

        const buttons = visibleItems.map((item, i) => {
            const icon = ACTION_BUTTON_ICONS[item.icon] || ACTION_BUTTON_ICONS.VerifiedUser;
            return <ActionButton
                id={`preVisitAction-${i}`}
                key={`preVisitAction-${item.dataId || 'nodata'}-${i}`}
                metric={`preVisitAction-${item.dataId || i}`}
                appContext={appContext}
                to='/conversation'
                onClick={() => this.onClickButton(item)}
                chevron={true}
                icon={icon}
                color={item.color || '#51cc42'}
                textColor={item.color || '#51cc42'}
                backgroundColor={item.bgColor || undefined}
                label={item.title || ''}
                label2={item.description || ''}
            />;
        });

        const content = visibleItems.length > 0
            ? <Grid container>{buttons}</Grid>
            : <div style={styles.caughtUp}>
                {typeof daysUntilHomeVisit === 'number'
                    ? T.translate('preVisit-allCaughtUp', { days: daysUntilHomeVisit })
                    : T.translate('preVisit-allCaughtUpNoVisit')}
            </div>;

        return <Grid item xs={12} style={{ width: '100%' }}>
            <Paper variant='outlined' style={styles.section}>
                <div style={styles.header}><T.span text='preVisit-header' /></div>
                {content}
            </Paper>
        </Grid>;
    }
}

const styles = {
    section: {
        marginLeft: 15,
        marginRight: 15,
        marginTop: 5,
        marginBottom: 5,
        padding: 10
    },
    header: {
        fontSize: 12,
        fontWeight: 600,
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: 1,
        margin: '4px 11px 8px'
    },
    caughtUp: {
        padding: '20px 12px',
        textAlign: 'center',
        color: '#666',
        fontSize: 14
    }
};

export default PreVisitActions;
